import { Box } from '@mui/material';
import { useSettings } from 'contexts/Settings/Settings';
import * as helpers from 'helpers';
import { isLessonPath } from 'helpers';
import { useRouter } from 'next/router';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { footer } from 'setup/footer';
import { general, metrics } from 'setup/general';
import { header } from 'setup/header';
import { landing } from 'setup/landing';

function getDefaultData(lessons: any): ContentData {
    return {
        footer: { ...footer },
        general: { ...general },
        header: { ...header },
        landing: { ...landing },
        lessons,
    };
}

const ContentContext = createContext<ContentState>({} as ContentState);

export const useContent = () => useContext(ContentContext);

export function withContent(WrappedComponent: any) {
    return function ContentWrappedComponent(props: any) {
        return (
            <ContentContext.Consumer>
                {state => <WrappedComponent {...props} content={state} />}
            </ContentContext.Consumer>
        );
    };
}

export function ContentProvider({ children }: ContentProviderProps) {
    const [data, setData] = useState<ContentData>(() => getDefaultData({}));
    const [loadError, setLoadError] = useState(false);
    const [ready, setReady] = useState(false);
    const settings = useSettings();
    const router = useRouter();
    const lessonRoute = useMemo(() => isLessonPath(router.asPath || router.pathname || '/'), [router.asPath, router.pathname]);

    useEffect(() => {
        let canceled = false;

        async function loadContent() {
            setReady(false);
            setLoadError(false);

            let lessons = {};
            let loadFailed = false;

            if (lessonRoute) {
                try {
                    lessons = (await import('setup/lessons')).lessons;
                } catch (error) {
                    loadFailed = true;
                }
            }
            if (canceled) {
                return;
            }

            const newData = getDefaultData(lessons);

            // Apply metric values
            newData.general.metricName = settings.metricName || newData.general.metricName;
            newData.general.metric = metrics[newData.general.metricName];
            newData.general.costUnit = newData.general.currency;
            newData.general.impactLabel = newData.general.metric.impactLabel;
            newData.general.impactUnit = newData.general.metric.impactType === 'currency' ? newData.general.currency : '';
            newData.general.profitUnit = newData.general.impactUnit;
            if (!loadFailed) {
                setData(newData);
            }
            setLoadError(loadFailed);
            setReady(true);
        }

        void loadContent();

        return () => {
            canceled = true;
        };
    }, [lessonRoute, settings.metricName]);

    function formatMetricValue(value?: number) {
        return helpers.formatMetricValue(data.general.metric.unit, value);
    }

    function formatValue(type: ValueType, value?: number) {
        const unit = (data.general as any)[`${type}Unit`];
        return helpers.formatValue(unit, value);
    }

    function getMetricValue(impact: number, cost: number) {
        return helpers.getMetricValue(data.general.metric, impact, cost);
    }

    function getProfitValue(impact: number, cost: number) {
        return helpers.getProfitValue(data.general.metric, impact, cost);
    }

    function parseHtml(html: string, variables?: Variables) {
        const vars = settings.debugContentVariables
            ? {}
            : {
                  ...data.general,
                  ...variables,
              };
        return helpers.parseHtml(html, vars);
    }

    const state = {
        ...data,
        formatMetricValue,
        formatValue,
        getMetricValue,
        getProfitValue,
        parseHtml,
    };

    if (!ready) {
        return <ContentContext.Provider value={state} />;
    }

    return (
        <ContentContext.Provider value={state}>
            {loadError ? (
                <Box data-testid="content-load-error" role="alert" sx={{ padding: 3, textAlign: 'center' }}>
                    Unable to load lesson content. Please refresh the page.
                </Box>
            ) : (
                children
            )}
        </ContentContext.Provider>
    );
}
